import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';
import * as objectPath from 'object-path';
import { Subscription, Subject, Observable } from 'rxjs';
import { tap, takeUntil, skip } from 'rxjs/operators';

import {
  LayoutConfigService,
  MenuConfigService,
  PageConfigService,
} from '@app/core/_base/layout';
import { LayoutConfig } from '@app/core/_config/channics-sidebar/layout.config';
import { menuConfig } from '@app/core/_config/channics-sidebar/menu.config';
import { PageConfig } from '@app/core/_config/channics-sidebar/page.config';
import { LocalStorageKeys } from '@app/core/_constant/local-storage.constants';
import { PermissionService } from '@app/core/auth/services/permission.service';
import { AppState } from '@app/core/reducers';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

import { HtmlClassService } from '../html-class.service';

@Component({
  selector: 'kt-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BaseComponent implements OnInit, OnDestroy {
  selfLayout :string;
  asideDisplay :boolean;
  asideSecondary :boolean;
  subheaderDisplay :boolean;
  permissions$ :Observable<RPermission>;

  private unsubscribe :Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private onDestroy$ = new Subject();

  constructor(
    private layoutConfigService :LayoutConfigService,
    private menuConfigService :MenuConfigService,
    private pageConfigService :PageConfigService,
    private htmlClassService :HtmlClassService,
    private store :Store<AppState>,
    private ft :FeatureToggleService,
    private permissionSvc :PermissionService,
    private permissionsService :NgxPermissionsService,
  ) {
    // register configs by demos
    this.layoutConfigService.loadConfigs(new LayoutConfig().configs);
    this.pageConfigService.loadConfigs(new PageConfig().configs);

    // setup element classes
    this.htmlClassService.setConfig(this.layoutConfigService.getConfig());

    const layoutSubscription = this.layoutConfigService.onConfigUpdated$.subscribe(
      (layoutConfig) => {
        // reset body class based on global and page level layout config, refer to html-class.service.ts
        document.body.className = '';
        this.htmlClassService.setConfig(layoutConfig);
      },
    );

    this.unsubscribe.push(layoutSubscription);
  }

  loadMenuConfig(rPermissions :RPermission) {
    const fts = {};

    let displayPageSettings = localStorage.getItem(
      LocalStorageKeys.DISPLAY_PAGE_SETTINGS,
    );
    if (displayPageSettings) {
      displayPageSettings = JSON.parse(displayPageSettings);
    }

    this.permissionsService.permissions$
      .pipe(skip(1), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.menuConfigService.loadConfigs(
          menuConfig(fts),
          rPermissions,
          displayPageSettings,
        );
      });
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() :void {
    const config = this.layoutConfigService.getConfig();
    this.permissions$ = this.permissionSvc.rPermissions$;
    this.getPermissions().subscribe();
    this.selfLayout = objectPath.get(config, 'self.layout');
    this.asideDisplay = objectPath.get(config, 'aside.self.display');
    this.subheaderDisplay = objectPath.get(config, 'subheader.display');
    this.permissionSvc.getRbac();

    // let the layout type change
    const layoutConfigSubscription =
      this.layoutConfigService.onConfigUpdated$.subscribe((cfg) => {
        setTimeout(() => {
          this.selfLayout = objectPath.get(cfg, 'self.layout');
        });
      });
    this.unsubscribe.push(layoutConfigSubscription);
  }

  /**
   * On destroy
   */
  ngOnDestroy() :void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private getPermissions() :Observable<RPermission> {
    return this.permissions$.pipe(
      tap((permissions) => {
        this.loadMenuConfig(permissions);
        this.setPermissions(permissions);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private setPermissions(permissions :RPermission) :void {
    const perm :string[] = [];
    Object.keys(permissions).map((key) => {
      if (permissions[key]) {
        perm.push(key);
      }
    });
    this.permissionsService.loadPermissions(perm);
  }
}
